/** Paddle REST prices are integer minor-unit strings, not decimal major amounts.
 * Paddle.js checkout totals use a separate converter in paddle-money.ts.
 */
import { parsePaddleApiMinor, paddleCurrencyExponent, paddleMinorToMajor } from './paddle-money';

/** @deprecated Historical name retained for consumers; prefer parsePaddleApiMinor.
 * No currency scaling is performed. Invalid amounts remain null, including decimal strings.
 */
export function paddleAmountToCents(amount: string): number | null {
  return parsePaddleApiMinor(amount);
}

/** Serialize a safe integer minor amount for Paddle REST. Despite the legacy name,
 * this works for zero-decimal currencies too. Invalid input must not become a price.
 */
export function centsToPaddleAmount(amount: number): string {
  if (!Number.isSafeInteger(amount) || amount < 0) throw new Error('Invalid Paddle minor amount');
  return String(amount);
}

/**
 * Days per interval for trial period conversion
 */
const DAYS_PER_INTERVAL: Record<string, number> = {
  day: 1,
  week: 7,
  month: 30, // Approximate
  year: 365, // Approximate
};

/**
 * Convert Paddle trial period to days count
 *
 * Paddle uses { interval, frequency } format for trial periods,
 * but @trialDays and @fullTrialDays need a days count.
 *
 * @param trialPeriod - Paddle trial period object
 * @returns Number of days
 *
 * @example
 * paddleTrialToDays({ interval: 'day', frequency: 7 })   // → 7
 * paddleTrialToDays({ interval: 'week', frequency: 1 })  // → 7
 * paddleTrialToDays({ interval: 'month', frequency: 1 }) // → 30
 */
export function paddleTrialToDays(
  trialPeriod: { interval: string; frequency: number } | undefined
): number {
  if (!trialPeriod) {
    return 0;
  }

  const daysPerUnit = DAYS_PER_INTERVAL[trialPeriod.interval] || 1;
  return daysPerUnit * trialPeriod.frequency;
}

/**
 * Convert days to Paddle trial period format
 *
 * Tries to find the most human-friendly interval:
 * - 7 days → { interval: 'week', frequency: 1 }
 * - 14 days → { interval: 'week', frequency: 2 }
 * - 30 days → { interval: 'month', frequency: 1 }
 * - 5 days → { interval: 'day', frequency: 5 }
 *
 * @param days - Number of trial days
 * @returns Paddle trial period object
 */
export function daysToPaddleTrialPeriod(
  days: number
): { interval: 'day' | 'week' | 'month' | 'year'; frequency: number } {
  // Check for exact month match (30 days)
  if (days % 30 === 0) {
    return { interval: 'month', frequency: days / 30 };
  }

  // Check for exact week match
  if (days % 7 === 0) {
    return { interval: 'week', frequency: days / 7 };
  }

  // Default to days
  return { interval: 'day', frequency: days };
}

/**
 * Convert Paddle billingCycle.frequency to Stripe-style intervalCount
 *
 * Both use the same concept (how many intervals per billing period),
 * just different naming.
 *
 * @param billingCycle - Paddle billing cycle
 * @returns Interval count (1 if not recurring)
 */
export function paddleFrequencyToIntervalCount(
  billingCycle: { interval: string; frequency: number } | undefined
): number {
  return billingCycle?.frequency || 1;
}

/**
 * Normalize Paddle currency code to lowercase (Stripe format)
 *
 * Paddle uses uppercase ("USD"), Stripe uses lowercase ("usd").
 * Most Intl.NumberFormat implementations work with either,
 * but we normalize to lowercase for consistency.
 *
 * @param currencyCode - Paddle currency code (uppercase)
 * @returns Normalized currency code (lowercase)
 */
export function normalizeCurrencyCode(currencyCode: string): string {
  return currencyCode.toLowerCase();
}

/** Format a Paddle REST minor-unit amount using the reviewed currency exponent.
 * Unknown/unsafe amounts or unsupported currencies produce no display value.
 */
export function formatPaddlePrice(amount: string, currencyCode: string, locale: string): string {
  const minor = parsePaddleApiMinor(amount);
  const exponent = paddleCurrencyExponent(currencyCode);
  if (minor === null || exponent === null) return '';
  const major = paddleMinorToMajor(minor, currencyCode);
  if (major === null) return '';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency: currencyCode.toUpperCase(),
      minimumFractionDigits: exponent, maximumFractionDigits: exponent,
    }).format(major);
  } catch {
    // A malformed locale cannot change the known amount or currency units.
    return `${currencyCode.toUpperCase()} ${major.toFixed(exponent)}`;
  }
}

/**
 * Check if Paddle billing cycle represents a subscription
 *
 * @param billingCycle - Paddle billing cycle (undefined for one-time)
 * @returns true if this is a recurring subscription
 */
export function isSubscription(
  billingCycle: { interval: string; frequency: number } | undefined
): boolean {
  return billingCycle !== undefined;
}

/**
 * Get Paddle interval as Stripe-compatible interval type
 *
 * @param billingCycle - Paddle billing cycle
 * @returns Interval type or undefined
 */
export function getPaddleInterval(
  billingCycle: { interval: string; frequency: number } | undefined
): 'day' | 'week' | 'month' | 'year' | undefined {
  if (!billingCycle) return undefined;

  const interval = billingCycle.interval;
  if (['day', 'week', 'month', 'year'].includes(interval)) {
    return interval as 'day' | 'week' | 'month' | 'year';
  }
  return undefined;
}
